import React from 'react';
import { Loader2 } from 'lucide-react';

interface LoadingStateProps {
  message?: string;
  size?: 'sm' | 'md' | 'lg';
  fullPage?: boolean;
}

/**
 * LoadingState Component
 * 
 * Displays a loading spinner with optional message.
 * Use during data fetching or async operations.
 * 
 * @example
 * <LoadingState message="Loading your briefs..." />
 * <LoadingState fullPage message="Setting up your account" />
 */
export function LoadingState({
  message = 'Loading...',
  size = 'md',
  fullPage = false,
}: LoadingStateProps) {
  const sizeClasses = {
    sm: 'w-6 h-6',
    md: 'w-10 h-10',
    lg: 'w-16 h-16',
  };

  const containerClasses = fullPage
    ? 'min-h-screen flex items-center justify-center'
    : 'flex flex-col items-center justify-center py-12';

  return (
    <div 
      className={containerClasses}
      role="status"
      aria-live="polite"
      aria-busy="true"
    >
      <Loader2 
        className={`${sizeClasses[size]} text-teal animate-spin mb-4`} 
        aria-hidden="true"
      />
      <p className="text-slate">{message}</p>
      <span className="sr-only">{message}</span>
    </div>
  );
}

export default LoadingState;
